import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

// Iter 407 : message photo de profil affiche des l'ouverture de l'application
// (page de pointage), AVANT de pouvoir pointer. Skippable 9 fois, obligatoire au 10e.
const ProfilePhotoPrompt = () => {
  const { user, refreshUser } = useAuth();
  const [prompt, setPrompt] = useState(null);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);

  useEffect(() => {
    // Iter 556 : les gestionnaires (structure administrative) sont exemptes de photo de profil
    if (!user?.id || user.is_gestionnaire || !['employee', 'store_admin', 'super_admin'].includes(user.role) || user.profile_photo_url || user.photo_url) return;
    const token = localStorage.getItem('token');
    axios.post(`${API}/employee/photo-prompt-seen`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.data.has_photo) setPrompt(res.data); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      toast.error('Le fichier doit etre une image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande (max 5 Mo)');
      return;
    }
    const loadingId = toast.loading('Vérification de la photo en cours...');
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/employee/profile-photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.dismiss(loadingId);
      toast.success('Photo de profil mise a jour');
      setPrompt(null);
      await refreshUser();
    } catch (err) {
      toast.dismiss(loadingId);
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'upload', { duration: 6000 });
    }
  };

  if (!prompt || !user || user.profile_photo_url || user.photo_url) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" data-testid="photo-prompt-modal">
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="absolute opacity-0 pointer-events-none w-0 h-0"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          await handleUpload(f);
          e.target.value = '';
        }}
        data-testid="photo-prompt-camera-input"
      />
      <input
        ref={libraryInputRef}
        type="file"
        accept="image/*"
        className="absolute opacity-0 pointer-events-none w-0 h-0"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          await handleUpload(f);
          e.target.value = '';
        }}
        data-testid="photo-prompt-library-input"
      />
      <div className="bg-white w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl">
        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-brand-blue/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h3 className="font-heading text-lg text-gray-900 mb-1">
          {prompt.must_add ? 'Photo de profil obligatoire' : 'Ajoutez votre photo de profil'}
        </h3>
        <p className="text-sm text-gray-500 mb-3">
          {prompt.must_add
            ? 'Vous devez prendre votre photo de profil pour pouvoir pointer et utiliser l\'application.'
            : 'Prenez-vous en photo avant de pointer : elle remplacera vos initiales dans toute l\'application.'}
        </p>
        {!prompt.must_add && (
          <p className="text-[11px] text-amber-600 font-semibold mb-4" data-testid="photo-prompt-count">
            Rappel {prompt.count}/10 — la photo deviendra obligatoire au 10e rappel.
          </p>
        )}
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white py-3 rounded-xl font-semibold transition"
          data-testid="photo-prompt-take"
        >
          Me prendre en photo
        </button>
        <button
          type="button"
          onClick={() => libraryInputRef.current?.click()}
          className="w-full mt-2 border border-brand-blue text-brand-blue py-3 rounded-xl font-semibold hover:bg-brand-blue/5 transition"
          data-testid="photo-prompt-library"
        >
          Choisir dans la photothèque
        </button>
        {!prompt.must_add && (
          <button
            type="button"
            onClick={() => setPrompt(null)}
            className="w-full mt-2 py-2.5 text-sm text-gray-500 hover:text-gray-800"
            data-testid="photo-prompt-skip"
          >
            Plus tard
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfilePhotoPrompt;
